import { type HTMLAttributes, type ReactElement, type ReactNode } from "react";
import { cn } from "@/lib/utils";
import { Eyebrow } from "./Eyebrow";
import { type H1Size } from "./H1Size";

const SIZE_CLASSES: Record<H1Size, string> = {
    sm: "text-2xl",
    md: "text-[34px]",
    lg: "text-4xl",
};

interface Props extends HTMLAttributes<HTMLHeadingElement> {
    children: ReactNode;
    size?: H1Size;
    /**
     * Identifier titles (order numbers, claim codes) are data, so they read in
     * mono rather than the serif display face — the same rule as every other
     * machine-ish value on the platform.
     */
    mono?: boolean;
    /**
     * Optional mono eyebrow rendered above the title, e.g. `CRM · ACCOUNTS`.
     * When set, eyebrow and title are wrapped in one block element so the pair
     * stays stacked even inside a flex row.
     */
    eyebrow?: ReactNode;
}

/**
 * The platform's page / display title. There is exactly one per screen, and it
 * is the ONLY heading allowed to use the serif display face — every
 * subheading ({@link H2} and below) is sans. Never write a raw `<h1>`.
 *
 * ```tsx
 * <H1>{t("vehicles.title")}</H1>
 * <H1 eyebrow="CRM · Accounts">{account.name}</H1>
 * <H1 mono size="sm">{order.order_number}</H1>
 * ```
 */
export function H1({ children, size = "md", mono = false, eyebrow, className, ...rest }: Props): ReactElement {
    const heading = (
        <h1
            className={cn(
                "leading-tight tracking-tight text-foreground",
                mono ? "font-mono font-semibold tabular-nums" : "font-serif font-normal",
                SIZE_CLASSES[size],
                className,
            )}
            {...rest}
        >
            {children}
        </h1>
    );

    if (eyebrow === undefined || eyebrow === null || eyebrow === false) {
        return heading;
    }

    return (
        <div className="min-w-0">
            <Eyebrow className="mb-1">{eyebrow}</Eyebrow>
            {heading}
        </div>
    );
}
